import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Lock, Tag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { useMyLikes } from "@/hooks/use-my-likes";
import { getThumbUrl, handleThumbError } from "@/lib/image-url";
import { kitPath } from "@/lib/kit-slug";

const CONDITION_PL: Record<string, string> = {
  "New with tags": "Nowa z metkami",
  "New without tags": "Nowa bez metek",
  "Excellent": "Bardzo dobry",
  "Good": "Dobry",
  "Fair": "Przeciętny",
};

const KIT_TYPE_PL: Record<string, string> = {
  "Home": "Domowa",
  "Away": "Wyjazdowa",
  "Third": "Trzecia",
  "Fourth": "Czwarta",
  "GK": "Bramkarska",
  "Special": "Okolicznościowa",
  "Training": "Treningowa",
};

interface KitCardProps {
  id: string;
  club: string;
  playerName?: string | null;
  season: string;
  brand: string;
  kitType: string;
  shirtVersion?: string | null;
  size?: string;
  condition?: string;
  estimatedValue?: number;
  salePrice?: number;
  purchaseCurrency?: string;
  tags?: string[];
  photoUrl?: string;
  likesCount?: number;
  isLikedByMe?: boolean;
  isPublic?: boolean;
  showPrice?: boolean;
  forSale?: boolean;
}

export default function KitCard({
  id,
  club,
  playerName,
  season,
  brand,
  kitType,
  shirtVersion,
  size,
  condition,
  estimatedValue,
  salePrice,
  purchaseCurrency = "PLN",
  tags = [],
  photoUrl,
  likesCount = 0,
  isLikedByMe = false,
  isPublic = true,
  showPrice = true,
  forSale = false,
}: KitCardProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isLiked, setLike } = useMyLikes();
  const [likes, setLikes] = useState(likesCount);
  const [likeBusy, setLikeBusy] = useState(false);
  const liked = isLiked(id, isLikedByMe);

  useEffect(() => {
    setLikes(likesCount);
  }, [likesCount]);

  const toggleLike = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (likeBusy) return;
    if (!user) {
      navigate("/auth");
      return;
    }

    setLikeBusy(true);

    const { data: existingLike, error: existingLikeError } = await supabase
      .from("kit_likes")
      .select("id")
      .eq("kit_id", id)
      .eq("user_id", user.id)
      .maybeSingle();

    if (existingLikeError) {
      setLikeBusy(false);
      return;
    }

    if (existingLike) {
      const { error } = await supabase.from("kit_likes").delete().eq("id", existingLike.id);
      if (!error) {
        setLike(id, false);
        setLikes((current) => Math.max(0, current - 1));
      }
    } else {
      const { error } = await supabase.from("kit_likes").insert({ kit_id: id, user_id: user.id });
      if (!error) {
        setLike(id, true);
        setLikes((current) => current + 1);
      }
    }

    setLikeBusy(false);
  };

  const currencySymbol = purchaseCurrency === "GBP" ? "£" : purchaseCurrency === "EUR" ? "€" : purchaseCurrency === "PLN" ? "zł" : "$";

  return (
    <Link to={kitPath(id, club, season, kitType)} className="group block h-full min-w-0">
      <div className="card-hover flex h-full min-w-0 flex-col overflow-hidden rounded-xl border bg-card shadow-sm">
        <div className="relative aspect-[4/5] w-full min-w-0 overflow-hidden bg-muted sm:aspect-[4/3]">
          {!isPublic && (
            <div className="absolute top-2 left-2 z-10 flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-[10px] font-medium text-white">
              <Lock className="h-3 w-3" /> Prywatna
            </div>
          )}
          {photoUrl ? (
            <img
              src={getThumbUrl(photoUrl)}
              onError={handleThumbError(photoUrl)}
              alt={`${club} ${season} ${KIT_TYPE_PL[kitType] || kitType}`}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-contain"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Tag className="h-12 w-12 text-muted-foreground/30" />
            </div>
          )}
          {forSale && (
            <div className="absolute inset-x-0 bottom-0 z-10 bg-white/75 px-2 py-1.5 text-center text-[10px] font-bold uppercase leading-tight tracking-normal text-pitch-dark backdrop-blur-sm sm:text-xs sm:tracking-wider">
              <span className="block truncate">
                {salePrice != null ? `Na sprzedaż ${salePrice.toFixed(0)} zł` : "Na sprzedaż"}
              </span>
            </div>
          )}
        </div>
        <div className="p-2.5 sm:p-4">
          <div className="flex items-center justify-between gap-1.5">
            <h3 className="min-w-0 text-xs font-bold leading-tight text-card-foreground sm:truncate sm:text-sm">{club}</h3>
            <button
              type="button"
              aria-label={liked ? "Usuń polubienie" : "Polub koszulkę"}
              onClick={toggleLike}
              disabled={likeBusy}
              className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-destructive disabled:pointer-events-none disabled:opacity-60"
            >
              <Heart className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${liked ? "fill-destructive text-destructive" : ""}`} />
              {likes > 0 && <span className="text-xs">{likes}</span>}
            </button>
          </div>
          <div className="space-y-0.5">
            {playerName && (
              <p className="text-[11px] font-medium leading-tight text-foreground/80 sm:truncate sm:text-xs">{playerName}</p>
            )}
            <p className="text-[11px] leading-tight text-muted-foreground sm:truncate sm:text-xs">{[brand, size].filter(Boolean).join(" · ") || "—"}</p>
            {shirtVersion && (
              <p className="text-[11px] leading-tight text-emerald-600 sm:text-xs">{shirtVersion}</p>
            )}
            {condition && (
              <p className="text-[11px] leading-tight text-muted-foreground sm:text-xs">{CONDITION_PL[condition] || condition}</p>
            )}
          </div>
          {showPrice && !forSale && estimatedValue != null && (
            <div className="mt-2 sm:mt-3">
              <span className="scoreboard text-sm text-primary sm:text-lg">
                {estimatedValue.toFixed(0)} {currencySymbol}
              </span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

